import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import * as XLSX from 'xlsx';
import {
  ExerciseQuestionBatchImportPayload,
  ExerciseQuestionBatchImportResult,
  ExerciseService,
} from '../../../../../services/exercise.service';
import { UserService } from '../../../../../services/user.service';

type ParsedImportRow = {
  rowNo: number;
  exerciseIdRaw: string;
  exerciseId: number | null;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  answerRaw: string;
  answerIndex: number | null;
  analysis: string;
  status: 'valid' | 'invalid';
  reason: string;
};

@Component({
    selector: 'app-admin-resource-exercise-batch-import',
    templateUrl: './index.component.html',
    styleUrls: ['./index.component.scss'],
    standalone: false
})
export class AdminResourceExerciseBatchImportComponent {
  readonly backUrl = '/admin/v3/resource/exercise/list';
  readonly templateDownloadUrl = 'https://hw-cdn.ireadabc.com/exercise-question-import-template-260507.xlsx';

  uploadFileName = '';
  parsing = false;
  submitting = false;
  parsedRows: ParsedImportRow[] = [];
  submitResult: ExerciseQuestionBatchImportResult | null = null;

  private readonly user: any;

  constructor(
    private readonly router: Router,
    private readonly msg: NzMessageService,
    private readonly userService: UserService,
    private readonly exerciseService: ExerciseService,
  ) {
    this.user = this.userService.getUserInfo();
  }

  get totalRows(): number {
    return this.parsedRows.length;
  }

  get validRows(): ParsedImportRow[] {
    return this.parsedRows.filter((row) => row.status === 'valid');
  }

  get invalidRows(): ParsedImportRow[] {
    return this.parsedRows.filter((row) => row.status === 'invalid');
  }

  get canSubmit(): boolean {
    return this.validRows.length > 0 && this.invalidRows.length === 0 && !this.submitting;
  }

  optionsText(row: ParsedImportRow): string {
    return this.buildOptions(row).join(' / ');
  }

  cancel(): void {
    this.router.navigate([this.backUrl]);
  }

  downloadTemplate(): void {
    if (this.templateDownloadUrl) {
      window.open(this.templateDownloadUrl, '_blank');
      return;
    }

    const rows = [
      ['题包id', '题干', '选项1', '选项2', '选项3', '选项4', '答案', '解析', '说明'],
      [123, 'this is a test', 'yes', 'no', '', '', '1', 'test', '示例行仅参考，不要删除，从第三行开始为导入数据。'],
      ['', '', '', '', '', '', '', '', '每次最多导入900行'],
    ];

    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, sheet, '练习导入模板');
    XLSX.writeFile(workbook, 'exercise-question-import-template.xlsx');
  }

  beforeUpload = (file: any) => {
    this.readExcel(file);
    return false;
  };

  submit(): void {
    if (!this.canSubmit) {
      this.msg.warning('请先修正模板中的错误后再提交');
      return;
    }

    const payload: ExerciseQuestionBatchImportPayload = {
      schoolId: this.user?.school_id || 0,
      rows: this.validRows.map((row) => ({
        rowNo: row.rowNo,
        exerciseId: row.exerciseId as number,
        question: row.question,
        options: this.buildOptions(row),
        answerIndex: row.answerIndex as number,
        analysis: row.analysis,
      })),
    };

    this.submitting = true;
    this.submitResult = null;
    this.exerciseService.batchImportQuestions(payload).then((result) => {
      this.submitResult = {
        successCount: result?.successCount || 0,
        failureCount: result?.failureCount || 0,
        failures: Array.isArray(result?.failures) ? result.failures : [],
      };
      this.submitting = false;
      this.msg.success('导入请求已完成');
    }).catch((err: any) => {
      this.submitting = false;
      this.submitResult = {
        successCount: 0,
        failureCount: payload.rows.length,
        failures: this.extractFailures(err),
      };
      this.msg.error(err?.message || '导入失败');
    });
  }

  private readExcel(file: any): void {
    this.uploadFileName = file?.name || '';
    this.parsedRows = [];
    this.submitResult = null;
    this.parsing = true;

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const raw = event.target?.result;
        const workbook = XLSX.read(raw, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as any[][];
        this.parsedRows = this.parseMatrix(matrix);
      } catch (err: any) {
        this.parsedRows = [{
          rowNo: 0,
          exerciseIdRaw: '',
          exerciseId: null,
          question: '',
          option1: '',
          option2: '',
          option3: '',
          option4: '',
          answerRaw: '',
          answerIndex: null,
          analysis: '',
          status: 'invalid',
          reason: err?.message || 'Excel 解析失败',
        }];
      } finally {
        this.parsing = false;
      }
    };

    reader.onerror = () => {
      this.parsing = false;
      this.msg.error('文件读取失败');
    };

    reader.readAsBinaryString(file);
  }

  private parseMatrix(matrix: any[][]): ParsedImportRow[] {
    const rows = (matrix || []).slice(2)
      .map((row, index) => this.parseDataRow(row || [], index + 3))
      .filter((row) => !!row) as ParsedImportRow[];

    if (rows.length === 0) {
      return [];
    }

    if (rows.length > 900) {
      return rows.map((row, index) => {
        if (index >= 900) {
          return {
            ...row,
            status: 'invalid',
            reason: '超过单次最多导入 900 行限制',
          };
        }
        return row;
      });
    }

    return rows;
  }

  private parseDataRow(row: any[], rowNo: number): ParsedImportRow | null {
    const normalized = row.map((item) => this.normalizeText(item));
    const isEmpty = normalized.slice(0, 8).every((item) => item === '');
    if (isEmpty) {
      return null;
    }

    const exerciseIdRaw = normalized[0] || '';
    const question = normalized[1] || '';
    const option1 = normalized[2] || '';
    const option2 = normalized[3] || '';
    const option3 = normalized[4] || '';
    const option4 = normalized[5] || '';
    const answerRaw = (normalized[6] || '').toUpperCase();
    const analysis = normalized[7] || '';

    const reasons: string[] = [];

    const exerciseId = /^\d+$/.test(exerciseIdRaw) ? parseInt(exerciseIdRaw, 10) : null;
    if (!exerciseId) {
      reasons.push('题包id 必须为正整数');
    }
    if (!question) {
      reasons.push('题干不能为空');
    }
    if (!option1) {
      reasons.push('选项1不能为空');
    }
    if (!option2) {
      reasons.push('选项2不能为空');
    }
    if (!option3 && option4) {
      reasons.push('选项3为空时不能填写选项4');
    }

    const answerIndex = this.resolveAnswerIndex(answerRaw);
    if (answerIndex === null) {
      reasons.push('答案仅支持 1/2/3/4 或 A/B/C/D');
    }

    const options = [option1, option2, option3, option4].filter((item) => !!item);
    if (answerIndex !== null && answerIndex >= options.length) {
      reasons.push('答案超出已填写选项范围');
    }

    return {
      rowNo,
      exerciseIdRaw,
      exerciseId,
      question,
      option1,
      option2,
      option3,
      option4,
      answerRaw,
      answerIndex,
      analysis,
      status: reasons.length > 0 ? 'invalid' : 'valid',
      reason: reasons.join('；'),
    };
  }

  private normalizeText(value: any): string {
    return `${value ?? ''}`.trim();
  }

  private resolveAnswerIndex(answer: string): number | null {
    const map: Record<string, number> = {
      '1': 0,
      '2': 1,
      '3': 2,
      '4': 3,
      A: 0,
      B: 1,
      C: 2,
      D: 3,
    };
    return typeof map[answer] === 'number' ? map[answer] : null;
  }

  private buildOptions(row: ParsedImportRow): string[] {
    return [row.option1, row.option2, row.option3, row.option4].filter((item) => !!item);
  }

  private extractFailures(err: any): Array<{ rowNo: number; reason: string }> {
    if (Array.isArray(err?.data?.failures)) {
      return err.data.failures;
    }
    if (Array.isArray(err?.failures)) {
      return err.failures;
    }
    return [{
      rowNo: 0,
      reason: err?.message || '导入失败',
    }];
  }
}
